import React, { useEffect } from "react";
import {
  Heart,
  ShieldCheck,
  Users,
  TrendingUp,
  CheckCircle2,
  Calendar,
  Award,
  ArrowRight,
  Activity,
  Microscope,
  MapPin,
  Clock,
  GraduationCap,
  Zap,
  Minus,
} from "lucide-react";
import airhoster from "../assets/sponsorship/airhoster.png";

const SponsorshipPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
 const stats = [
    "716 Million people in rural India lack adequate hygiene & healthcare access",
  "23 Million girls drop out of school due to menstrual stigma and pad inaccessibility",
  "1 in 5 girls never returns to school after her first period",
  "20–30% of women of reproductive age are affected by PCOD",
  "Up to 80% of PCOD cases remain undiagnosed in rural areas",
  "90% of rural women do not access preventive health screenings",
  ];

  return (
    <div className="bg-[#FFF0F3] min-h-screen font-sans text-slate-900 selection:bg-purple-200 overflow-x-hidden">
      {/* --- Hero Section (Philosophy from PDF 1) --- */}
      <section className="relative py-20 px-8 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-pink-200 text-purple-800 text-[11px] font-black mb-8 tracking-[0.2em] shadow-sm uppercase">
              {/* <Zap size={14} className="fill-purple-800" /> Positioning: Shared
              Responsibility */}
              SAKHI SPONSORSHIP
            </div>
            <h1 className="text-5xl lg:text-7xl font-black leading-[1.05] text-purple-950 mb-8">
              Be the reason her period is{" "}
              <span className="text-pink-600 italic">safe, dignified,</span> and
              worry-free.
            </h1>
            <p className="text-xl text-slate-600 mb-10 leading-relaxed font-medium">
              “I am supporting a woman — not funding a problem.” <br />
              <span className="text-base font-normal opacity-75">
                Eveya Sakhi ensures continuous access to period care, health
                support, and confidence — every month.
              </span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="bg-purple-800 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-purple-900 transition shadow-2xl shadow-purple-200 flex items-center justify-center gap-3">
                Sponsor Period Dignity <ArrowRight size={20} />
              </button>
            </div>
          </div>
          <div className="relative group">
            <div className="absolute inset-0 bg-white/50 rounded-[4rem] rotate-3 -z-10 border border-white"></div>
            <img
              src={airhoster}
              alt="Dignity"
              className="rounded-[4rem] shadow-2xl z-10 aspect-[4/5] object-cover border-8 border-white transition-transform group-hover:scale-[1.02] duration-500"
            />
          </div>
        </div>
      </section>

      {/* --- Continuous Circular Carousel (Statistics from PDF 2) --- */}
      <div className="bg-purple-950 py-8 border-y-4 border-pink-500 relative overflow-hidden flex">
        {/* We duplicate the content to ensure it never "ends" visually */}
        <div className="flex whitespace-nowrap animate-marquee">
          {[...stats, ...stats].map((stat, index) => (
            <div key={index} className="flex items-center px-10">
              <span className="text-white font-black uppercase tracking-widest text-sm opacity-90">
                {stat}
              </span>
              <Minus
                className="text-pink-500 mx-10 rotate-90"
                size={24}
                strokeWidth={4}
              />
            </div>
          ))}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 40s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `,
        }}
      />

      {/* --- SAKHI SPONSOR HEADING & IMPACT LEVELS --- */}
      <section id="tiers" className="py-24 px-8 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-black text-purple-950 mb-4 uppercase tracking-tighter">
            Choose Your <span className="text-pink-600">Impact Level</span>
          </h2>
          <p className="text-slate-500 font-bold text-lg">
            Sponsorship tiers for holistic support
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          <TierCard
            tier="Monthly"
            price="₹60"
            period="/month"
            subPrice="₹720/year"
            pads="10 Pads"
            benefits={[
              "Products",
              "Monthly wellness tips",
              "Flexibility to pause",
            ]}
            colorScheme="pink"
          />
          <TierCard
            tier="Quarterly"
            price="₹180"
            period="/quarter"
            subPrice="₹720/year"
            pads="30 Pads"
            benefits={["Products", "Quarterly updates", "Health consultation"]}
            colorScheme="pink"
          />
          <TierCard
            tier="Half-Yearly"
            price="₹360"
            period="/6 months"
            subPrice="₹720/year"
            pads="60 Pads"
            benefits={[
              "Products",
              "Education access",
              "Health consultations",
              "Peer support",
            ]}
            colorScheme="teal"
          />
          <TierCard
            tier="Annual"
            price="₹720"
            period="/year"
            subPrice="Full Package"
            pads="120 Pads"
            benefits={[
              "Products",
              "Monthly updates",
              "Health access",
              "Community support",
              "Annual report",
            ]}
            colorScheme="teal"
            featured={true}
          />
        </div>
      </section>

      {/* --- The 4 Pillars (Solution from PDF 2) --- */}
      {/* <section id="pillars" className="py-24 px-8 max-w-7xl mx-auto">
        <div className="mb-20 text-center">
          <h2 className="text-4xl font-black text-purple-950 mb-4 uppercase tracking-tighter">
            The Eveya Sakhi Model
          </h2>
          <p className="text-slate-500 font-bold italic">
            “Sakhi is built as a scalable sponsorship model — not one-time aid.”
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <PillarCard
            icon={<ShieldCheck className="text-pink-500" />}
            title="Pillar 1: Care"
            desc="120 Premium pads/year. Dioxin-free, breathable, and hormone-safe."
          />
          <PillarCard
            icon={<Microscope className="text-pink-600" />}
            title="Pillar 2: Screening"
            desc="PCOD, Diabetes, and Cardiac profiles via Thyrocare partnership."
          />
          <PillarCard
            icon={<MapPin className="text-pink-500" />}
            title="Pillar 3: Local Employment"
            desc="Female health coordinators hired from the village for monthly tracking."
          />
          <PillarCard
            icon={<Users className="text-pink-600" />}
            title="Pillar 4: Education"
            desc="Quarterly community awareness, myth-busting, and nutritional counseling."
          />
        </div>
      </section> */}

      {/* --- Sponsorship Tiers (Critical Design from PDF 1) --- */}
      <section id="tiers" className="py-24 px-8 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-black text-purple-950 mb-4 uppercase tracking-tighter">
            Sponsorship Plans
          </h2>
          <p className="text-slate-500 font-medium italic">
            Framed as "Continuity of Care" to build lasting confidence.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <TierCard1
            tier="Monthly Sakhi"
            // target="First-time"
            message="Ensure one woman never worries about her next cycle."
          />
          <TierCard1
            tier="Quarterly Sakhi"
            // target="Families"
            message="Consistency that builds lasting confidence."
          />
          <TierCard1
            tier="Half-Yearly Sakhi"
            // target="Teams"
            message="Stability that changes habits, not just outcomes."
          />
          <TierCard1
            tier="Annual Sakhi"
            // target="Investors"
            message="One year. One woman. Complete dignity."
            featured={true}
          />
        </div>
      </section>

  

     

      {/* --- Footer CTA (Final Positioning from PDF 1) --- */}
      <footer className="bg-white py-24 px-8 text-center rounded-t-[5rem] shadow-inner">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-5xl md:text-7xl font-black text-purple-950 mb-8 italic">
            Stand with her.
          </h2>
          <p className="text-xl text-slate-500 mb-12 font-medium italic">
            "Eveya Sakhi is not about giving pads. It's about ensuring no woman
            has to plan her life around uncertainty."
          </p>
          <button className="bg-purple-800 text-white px-12 py-6 rounded-3xl font-black text-2xl hover:bg-purple-900 transition shadow-2xl shadow-purple-200 active:scale-95">
            Become an Eveya Sakhi
          </button>

          <div className="mt-20 pt-12 border-t border-pink-50 flex flex-col md:flex-row justify-between items-center gap-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            <p>© Farmfit Agrihealth LLP</p>
            {/* <div className="flex gap-8">
              <p>Naga Vamsi Velagapudi</p>
              <p>+91 8977137767</p>
            </div> */}
          </div>
        </div>
      </footer>
    </div>
  );
};

/* --- Helper Components --- */

const PillarCard = ({ icon, title, desc }) => (
  <div className="bg-white p-8 rounded-[2.5rem] border border-pink-100 shadow-sm hover:shadow-xl transition-all group">
    <div className="w-14 h-14 bg-pink-50 rounded-2xl flex items-center justify-center text-purple-700 mb-6 group-hover:bg-purple-700 group-hover:text-white transition-colors">
      {React.cloneElement(icon, { size: 28 })}
    </div>
    <h4 className="text-lg font-black text-purple-950 mb-2 uppercase tracking-tighter">
      {title}
    </h4>
    <p className="text-xs text-slate-500 font-medium leading-relaxed">{desc}</p>
  </div>
);

const TierCard1 = ({ tier, target, message, featured = false }) => (
  <div
    className={`relative p-8 rounded-[3rem] transition-all duration-500 ${
      featured
        ? "bg-purple-800 text-white shadow-2xl scale-105 z-10"
        : "bg-white border border-pink-100 hover:shadow-lg"
    }`}
  >
    {featured && (
      <div className="absolute -top-4 right-10 bg-pink-500 text-white px-3 py-1 rounded-full text-[10px] font-black tracking-widest">
        MOST IMPACTFUL
      </div>
    )}
    {/* <p
      className={`text-[10px] font-black uppercase tracking-widest mb-1 ${featured ? "text-pink-300" : "text-slate-400"}`}
    >
      FOR: {target}
    </p> */}
    <h3 className="text-2xl font-black mb-4 uppercase tracking-tighter">
      {tier}
    </h3>
    <p
      className={`text-sm mb-10  leading-relaxed italic font-medium ${featured ? "text-white/80" : "text-slate-500"}`}
    >
      "{message}"
    </p>
    {/* <button
      className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${
        featured
          ? "bg-pink-500 text-white hover:bg-pink-600"
          : "bg-pink-50 text-purple-900 hover:bg-pink-100"
      }`}
    >
      Sponsor
    </button> */}
  </div>
);

const ImpactItem = ({ label, value }) => (
  <div className="flex justify-between items-center p-5 bg-white rounded-2xl border border-pink-100 mb-4">
    <span className="font-bold text-slate-600 text-sm">{label}</span>
    <span className="font-black text-purple-900">{value}</span>
  </div>
);
const TierCard = ({
  tier,
  price,
  period,
  subPrice,
  pads,
  benefits,
  colorScheme,
  featured,
}) => {
  const isPink = colorScheme === "pink";

  return (
    <div
      className={`flex flex-col p-8 rounded-[1rem] transition-all duration-300 border ${
        isPink ? "bg-[#ffebee] border-pink-100" : "bg-[#e0f2f1] border-teal-100"
      } hover:shadow-2xl`}
    >
      <div className="text-center mb-6">
        <h3
          className={`text-2xl font-bold mb-4 ${isPink ? "text-pink-600" : "text-teal-700"}`}
        >
          {tier}
        </h3>
        <div className="flex items-baseline justify-center">
          <span className="text-3xl font-black text-slate-800">{price}</span>
          <span className="text-slate-500 font-bold ml-1">{period}</span>
        </div>
      
      </div>

      <div className="h-px bg-slate-200 w-full mb-6"></div>

      <div className="flex-grow">
        <p className="text-center font-black text-slate-800 uppercase tracking-widest text-xs mb-6">
          Benefits
        </p>
        <div className="flex items-center gap-3 mb-4">
          <CheckCircle2
            size={16}
            className={isPink ? "text-pink-500" : "text-teal-600"}
          />
          <span className="text-sm font-bold text-slate-700">
            {pads} included
          </span>
        </div>
        {/* <ul className="space-y-4">
          {benefits.map((benefit, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle2
                size={16}
                className={isPink ? "text-pink-400" : "text-teal-500"}
              />
              <span className="text-sm text-slate-600 font-medium">
                {benefit}
              </span>
            </li>
          ))}
        </ul> */}
      </div>

      <button
        className={`w-full py-4 mt-8 rounded-lg font-black text-sm text-white uppercase tracking-widest transition-all ${
          isPink
            ? "bg-red-500 hover:bg-red-600 shadow-red-100"
            : "bg-[#008080] hover:bg-[#006666] shadow-teal-100"
        } shadow-lg`}
      >
        Choose {tier}
      </button>
    </div>
  );
};
const TimelinePoint = ({ month, text, active = false }) => (
  <div className="flex items-start gap-4 relative z-10">
    <div
      className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center border-2 ${active ? "bg-pink-500 border-pink-500 shadow-[0_0_15px_rgba(236,72,153,0.5)]" : "bg-purple-900 border-white/20"}`}
    >
      {active && <CheckCircle2 size={12} className="text-white" />}
    </div>
    <div>
      <span className="text-[10px] font-black uppercase text-pink-400">
        Month {month}
      </span>
      <p
        className={`text-sm font-medium ${active ? "text-white" : "text-white/40"}`}
      >
        {text}
      </p>
    </div>
  </div>
);

const MatrixRow = ({ activity, role1, role2 }) => (
  <div className="p-8 border-b border-pink-50 flex justify-between items-center hover:bg-pink-50/30 transition-colors">
    <span className="font-bold text-slate-700 text-sm">{activity}</span>
    <div className="flex gap-4">
      <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-[9px] font-black uppercase">
        {role1}
      </span>
      <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-[9px] font-black uppercase">
        {role2}
      </span>
    </div>
  </div>
);

export default SponsorshipPage;
